import { ROUTES } from "@/constants";
import { PLATFORM_BASE_DOMAIN } from "@/constants/platform";
import { getGlobalSiteOrigin } from "@/utils/tenant";

export type LinkHubInternalRouteKey =
  | "home"
  | "calendar"
  | "spaces"
  | "castings";

interface LinkHubInternalRoute {
  key: LinkHubInternalRouteKey;
  label: string;
  path: string;
  isGlobalSite: boolean;
}

export const LINKHUB_INTERNAL_DESTINATION_HINT =
  "Las opciones con * son del sitio principal de Tercer Espacio (no exclusivas de tu sala).";

export const LINKHUB_EXTERNAL_URL_HINT =
  "Enlace completo con https:// (redes sociales, web propia, etc.). Se abre fuera de tu landing.";

const LINKHUB_INTERNAL_ROUTES: LinkHubInternalRoute[] = [
  {
    key: "home",
    label: "Inicio",
    path: ROUTES.public.home,
    isGlobalSite: false,
  },
  {
    key: "calendar",
    label: "Calendario",
    path: ROUTES.public.calendar,
    isGlobalSite: false,
  },
  {
    key: "spaces",
    label: "Salas",
    path: ROUTES.public.spaces,
    isGlobalSite: true,
  },
  {
    key: "castings",
    label: "Castings",
    path: ROUTES.public.castings,
    isGlobalSite: true,
  },
];

const routeByKey = new Map(
  LINKHUB_INTERNAL_ROUTES.map((route) => [route.key, route]),
);

const routeByPath = new Map(
  LINKHUB_INTERNAL_ROUTES.map((route) => [route.path, route]),
);

const normalizePath = (pathname: string): string => {
  if (!pathname || pathname === "/") return ROUTES.public.home;
  return pathname.endsWith("/") && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
};

const isGlobalSiteHostname = (hostname: string): boolean => {
  const host = hostname.toLowerCase();
  const base = PLATFORM_BASE_DOMAIN.toLowerCase();
  return host === base || host === `www.${base}`;
};

/**
 * Options map for CmsSelectField (global destinations show trailing *).
 */
export const buildLinkHubInternalSelectOptions = (): Record<string, string> =>
  Object.fromEntries(
    LINKHUB_INTERNAL_ROUTES.map((route) => [
      route.key,
      route.isGlobalSite ? `${route.label} *` : route.label,
    ]),
  );

export interface LinkHubLinkPersistPayload {
  url: string;
  isExternalLink: boolean;
  trackKey: string;
}

/**
 * Maps a catalog key to API fields (main-site screens persist as external href).
 */
export const buildLinkHubPayloadFromInternalKey = (
  key: LinkHubInternalRouteKey,
): LinkHubLinkPersistPayload => {
  const route = routeByKey.get(key);
  if (!route) {
    throw new Error(`Unknown LinkHub internal route key: ${key}`);
  }

  if (route.isGlobalSite) {
    const origin = getGlobalSiteOrigin().replace(/\/$/, "");
    return {
      url: `${origin}${route.path}`,
      isExternalLink: true,
      trackKey: key,
    };
  }

  return {
    url: route.path,
    isExternalLink: false,
    trackKey: key,
  };
};

interface StoredLinkShape {
  url: string;
  isExternalLink: boolean;
}

/**
 * Resolves catalog key when editing; null means show generic external URL field.
 */
export const resolveInternalKeyFromStoredLink = (
  link: StoredLinkShape,
): LinkHubInternalRouteKey | null => {
  const raw = link.url.trim();
  if (!raw) return null;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      if (!isGlobalSiteHostname(parsed.hostname)) {
        return null;
      }
      const route = routeByPath.get(normalizePath(parsed.pathname));
      return route?.key ?? null;
    } catch {
      return null;
    }
  }

  const path = normalizePath(raw.startsWith("/") ? raw : `/${raw}`);
  const route = routeByPath.get(path);
  if (!route) return null;

  if (route.isGlobalSite) {
    return route.key;
  }

  if (link.isExternalLink) {
    return null;
  }

  return route.key;
};
